import {
  DeleteOutlined,
  EditOutlined,
  FormOutlined,
  MailOutlined,
  MoreOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Dropdown,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import {
  ContactChannel,
  ContactFollowUpStatus,
  createContactRequest,
  deleteContactRequest,
  listContactRequests,
  recordContactFollowUp,
  updateContactRequest,
} from '../api/contactRequests';
import type {
  ContactChannelValue,
  ContactFollowUpStatusValue,
  ContactRequestFilter,
  ContactRequestItem,
  ContactRequestPage as ContactRequestPageData,
} from '../api/contactRequests';
import { ApiError } from '../api/errors';
import { PermissionCodes } from '../auth/permissions';
import { useAuth } from '../auth/useAuth';
import { BrandColors } from '../theme/brand';
import { formatDateTime } from './projects/projectDisplay';

/** Tên và màu nhãn của từng tình trạng. */
const statusMeta: Readonly<Record<ContactFollowUpStatusValue, { label: string; color: string }>> = {
  [ContactFollowUpStatus.New]: { label: 'Chưa liên hệ', color: 'gold' },
  [ContactFollowUpStatus.InProgress]: { label: 'Đang liên hệ', color: 'processing' },
  [ContactFollowUpStatus.Contacted]: { label: 'Đã liên hệ', color: 'success' },
  [ContactFollowUpStatus.Unreachable]: { label: 'Không liên lạc được', color: 'orange' },
  [ContactFollowUpStatus.Irrelevant]: { label: 'Không phù hợp', color: 'default' },
};

/**
 * Tên dễ đọc của mã dịch vụ mà form liên hệ trên website gửi lên — chép đúng chữ của ô chọn dịch vụ
 * trên website. Mã lạ (website thêm dịch vụ mới) thì hiện nguyên mã, không giấu đi.
 */
const serviceNames: Readonly<Record<string, string>> = {
  'dao-tao': 'Đào tạo - Tư vấn khóa học',
  'cung-cap-giai-phap': 'Cung cấp - Tư vấn giải pháp',
};

/**
 * Màu nhãn dịch vụ (người dùng chọn): khách cần cung cấp, tư vấn giải pháp là mối quan trọng nhất,
 * nên nhãn đó nền đỏ thương hiệu đặc, chữ trắng đậm — nổi hẳn so với nhãn xanh nhạt của đào tạo.
 * Mã lạ thì nhãn xám.
 */
const serviceTagStyles: Readonly<Record<string, { color: string; strong: boolean }>> = {
  'dao-tao': { color: 'blue', strong: false },
  'cung-cap-giai-phap': { color: BrandColors.red, strong: true },
};

/**
 * Lựa chọn của ô dịch vụ khi sửa liên hệ: các mã form website dùng, cộng thêm mã hiện tại nếu đó là
 * mã lạ — để mở biểu mẫu rồi lưu mà không đổi gì thì không làm mất dịch vụ khách đã chọn.
 */
function serviceOptionsFor(current: string | null): { value: string; label: string }[] {
  const options = Object.entries(serviceNames).map(([value, label]) => ({ value, label }));

  return current === null || current === '' || Object.hasOwn(serviceNames, current)
    ? options
    : [...options, { value: current, label: `${current} (mã khác của website)` }];
}

/** Độ dài tối đa của mỗi ô chữ — bằng cột của website. */
const maximumFieldLength = 255;

/** Độ dài tối đa của lời nhắn — máy chủ chặn đúng mức này. */
const maximumMessageLength = 5000;

/** Dạng email máy chủ chấp nhận: `a@b` — đúng một `@`, hai bên có chữ, không khoảng trắng. */
const emailPattern = /^[^\s@]+@[^\s@]+$/;

/**
 * Lựa chọn của ô "Nguồn" khi thêm liên hệ nhập tay. Chữ trùng với nhãn máy chủ ghi ở đầu lời nhắn,
 * ví dụ "[Zalo] Cần báo giá".
 */
const channelOptions: readonly { value: ContactChannelValue; label: string }[] = [
  { value: ContactChannel.Zalo, label: 'Zalo' },
  { value: ContactChannel.Phone, label: 'Điện thoại' },
  { value: ContactChannel.Email, label: 'Email' },
  { value: ContactChannel.InPerson, label: 'Gặp trực tiếp' },
  { value: ContactChannel.Other, label: 'Khác' },
];

/**
 * Độ dài tối đa của lời nhắn khi thêm liên hệ: máy chủ ghép nhãn kênh và một khoảng trắng vào đầu
 * lời nhắn, cả hai cộng lại không vượt `maximumMessageLength`. Chừa chỗ cho nhãn dài nhất.
 */
const manualMessageMaximumLength =
  maximumMessageLength - Math.max(...channelOptions.map((option) => `[${option.label}] `.length));

/**
 * Giá trị của biểu mẫu thông tin liên hệ. Ô dịch vụ bỏ trống thì không có giá trị; ô nguồn chỉ có
 * khi thêm liên hệ.
 */
interface ContactFormValues {
  lastName: string;
  firstName: string;
  phone: string;
  email: string;
  service?: string;
  message: string;
  channel?: ContactChannelValue;
}

/** Biểu mẫu thông tin liên hệ đang mở: thêm liên hệ nhập tay, hoặc sửa một liên hệ có sẵn. */
type ContactDialog =
  | { readonly mode: 'create' }
  | { readonly mode: 'edit'; readonly item: ContactRequestItem };

/** Ô bỏ trống hoặc chỉ có khoảng trắng. */
function isBlank(value: unknown): boolean {
  return typeof value !== 'string' || value.trim() === '';
}

/** Cắt khoảng trắng; ô bỏ trống gửi lên là `null`. */
function blankToNull(value: string | undefined): string | null {
  const trimmed = value?.trim() ?? '';

  return trimmed === '' ? null : trimmed;
}

/** Thứ tự các lựa chọn trong hộp thoại ghi kết quả. */
const statusOptions: readonly ContactFollowUpStatusValue[] = [
  ContactFollowUpStatus.InProgress,
  ContactFollowUpStatus.Contacted,
  ContactFollowUpStatus.Unreachable,
  ContactFollowUpStatus.Irrelevant,
  ContactFollowUpStatus.New,
];

/** Các thẻ lọc. "Cần xử lý" đứng đầu và mở sẵn: trang này tồn tại để không ai bị bỏ sót. */
type TabKey = 'needs-action' | 'new' | 'in-progress' | 'unreachable' | 'contacted' | 'irrelevant' | 'all';

const tabFilters: Readonly<Record<TabKey, Pick<ContactRequestFilter, 'status' | 'needsAction'>>> = {
  'needs-action': { needsAction: true },
  new: { status: ContactFollowUpStatus.New },
  'in-progress': { status: ContactFollowUpStatus.InProgress },
  unreachable: { status: ContactFollowUpStatus.Unreachable },
  contacted: { status: ContactFollowUpStatus.Contacted },
  irrelevant: { status: ContactFollowUpStatus.Irrelevant },
  all: {},
};

const pageSize = 20;

/** Liên hệ chưa ai gọi lại sau chừng này ngày thì được tô đỏ. */
const overdueAfterDays = 2;

/** Số ngày đã trôi qua kể từ một mốc thời gian, tính theo lịch chứ không theo 24 giờ. */
function daysSince(value: string | null): number | null {
  if (value === null) {
    return null;
  }

  const moment = new Date(value);

  if (Number.isNaN(moment.getTime())) {
    return null;
  }

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfThatDay = new Date(moment);
  startOfThatDay.setHours(0, 0, 0, 0);

  return Math.round((startOfToday.getTime() - startOfThatDay.getTime()) / 86_400_000);
}

/**
 * Ngày theo giờ máy người xem, dạng 03/11/2026, không kèm giờ phút. Không dùng `formatDate` của dự
 * án: hàm đó cắt thẳng chuỗi ISO giờ UTC, nên liên hệ gửi trước 7 giờ sáng sẽ lùi sai một ngày.
 */
function formatLocalDate(value: string | null): string {
  if (value === null) {
    return '—';
  }

  const moment = new Date(value);

  return Number.isNaN(moment.getTime())
    ? '—'
    : moment.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Số ngày một liên hệ chưa ai gọi đã chờ, khi đã quá hạn; chưa quá hạn hoặc đã có người gọi thì null. */
function overdueDays(item: ContactRequestItem): number | null {
  const days = daysSince(item.submittedAt);

  return item.status === ContactFollowUpStatus.New && days !== null && days >= overdueAfterDays
    ? days
    : null;
}

/**
 * Nhãn tình trạng. Liên hệ chờ quá lâu mà chưa ai gọi thì nhãn đỏ "N ngày chưa liên hệ" thay hẳn
 * nhãn "Chưa liên hệ" — để cả hai là nói một điều hai lần.
 */
function statusTag(item: ContactRequestItem): ReactNode {
  const days = overdueDays(item);

  return days === null ? (
    <Tag color={statusMeta[item.status].color} style={{ marginInlineEnd: 0 }}>
      {statusMeta[item.status].label}
    </Tag>
  ) : (
    <Tag color="red" style={{ marginInlineEnd: 0, fontWeight: 600 }}>
      {days.toString()} ngày chưa liên hệ
    </Tag>
  );
}

/** Dòng phụ xám dưới ngày gửi: "Hôm nay", "Hôm qua", "3 ngày trước". */
function ageLabel(item: ContactRequestItem): ReactNode {
  const days = daysSince(item.submittedAt);

  if (days === null) {
    return null;
  }

  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {days <= 0 ? 'Hôm nay' : days === 1 ? 'Hôm qua' : `${days.toString()} ngày trước`}
    </Typography.Text>
  );
}

/**
 * Liên hệ khách hàng — những người để lại thông tin ở form liên hệ trên website công ty.
 *
 * Dữ liệu liên hệ nằm ở website: ERP đọc, và người có quyền thêm được liên hệ nhập tay (khách nhắn
 * Zalo, gọi điện…), sửa thông tin hoặc xóa mềm liên hệ ngay trên đó. Việc ai đã gọi, kết quả ra sao
 * ghi ở ERP. Trang mở sẵn
 * thẻ "Cần xử lý" — chưa liên hệ, đang liên hệ, chưa liên lạc được — vì mục đích của trang là không
 * để khách nào bị bỏ quên, chứ không phải để xem lại lịch sử.
 */
export function ContactRequestPage(): ReactNode {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PermissionCodes.ContactRequestManage);

  const [tab, setTab] = useState<TabKey>('needs-action');
  const [searchTerm, setSearchTerm] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [reloadToken, setReloadToken] = useState(0);

  const [data, setData] = useState<ContactRequestPageData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [editing, setEditing] = useState<ContactRequestItem | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<ContactFollowUpStatusValue>(
    ContactFollowUpStatus.Contacted,
  );
  const [note, setNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Thêm hoặc sửa thông tin liên hệ — khác với "editing" ở trên, vốn là liên hệ đang được ghi kết
  // quả.
  const [contactForm] = Form.useForm<ContactFormValues>();
  const [contactDialog, setContactDialog] = useState<ContactDialog | null>(null);
  const [isSavingContact, setIsSavingContact] = useState(false);
  const isCreatingContact = contactDialog?.mode === 'create';

  useEffect(() => {
    let isCurrent = true;

    async function load(): Promise<void> {
      setIsLoading(true);
      setErrorMessage(null);

      try {
        const page = await listContactRequests({
          ...tabFilters[tab],
          ...(searchTerm === '' ? {} : { searchTerm }),
          pageNumber,
          pageSize,
        });

        if (isCurrent) {
          setData(page);
        }
      } catch (error) {
        if (isCurrent) {
          setErrorMessage(
            error instanceof ApiError ? error.message : 'Không tải được danh sách liên hệ.',
          );
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      isCurrent = false;
    };
  }, [tab, searchTerm, pageNumber, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  function openRecord(item: ContactRequestItem): void {
    setEditing(item);
    // Liên hệ mới thì gợi ý "đang liên hệ" — người bấm nút thường vừa gọi xong lần đầu.
    setSelectedStatus(
      item.status === ContactFollowUpStatus.New ? ContactFollowUpStatus.InProgress : item.status,
    );
    setNote(item.note ?? '');
  }

  async function saveRecord(): Promise<void> {
    if (editing === null) {
      return;
    }

    setIsSaving(true);

    try {
      await recordContactFollowUp(editing.id, {
        status: selectedStatus,
        note: note.trim() === '' ? null : note.trim(),
      });
      message.success(`Đã ghi kết quả cho ${editing.fullName || 'liên hệ này'}.`);
      setEditing(null);
      reload();
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không ghi được kết quả.');
    } finally {
      setIsSaving(false);
    }
  }

  function openEditContact(item: ContactRequestItem): void {
    // Xóa sạch giá trị của lần sửa trước: biểu mẫu dùng chung một kho giá trị cho mọi liên hệ, và ô
    // dịch vụ để trống thì không được đặt lại ở dưới.
    contactForm.resetFields();
    contactForm.setFieldsValue({
      lastName: item.lastName ?? '',
      firstName: item.firstName ?? '',
      phone: item.phone ?? '',
      email: item.email ?? '',
      message: item.message ?? '',
      ...(item.service === null || item.service === '' ? {} : { service: item.service }),
    });
    setContactDialog({ mode: 'edit', item });
  }

  function openCreateContact(): void {
    contactForm.resetFields();
    contactForm.setFieldsValue({
      channel: ContactChannel.Zalo,
      lastName: '',
      firstName: '',
      phone: '',
      email: '',
      message: '',
    });
    setContactDialog({ mode: 'create' });
  }

  async function saveContact(values: ContactFormValues): Promise<void> {
    if (contactDialog === null) {
      return;
    }

    const details = {
      lastName: blankToNull(values.lastName),
      firstName: blankToNull(values.firstName),
      phone: blankToNull(values.phone),
      email: blankToNull(values.email),
      service: blankToNull(values.service),
      message: blankToNull(values.message),
    };

    setIsSavingContact(true);

    try {
      if (contactDialog.mode === 'create') {
        await createContactRequest({ ...details, channel: values.channel ?? ContactChannel.Zalo });
        message.success('Đã thêm liên hệ.');
        // Liên hệ mới mang tình trạng "Chưa liên hệ" và mới nhất: về trang đầu thẻ "Cần xử lý" là
        // thấy ngay ở dòng trên cùng.
        setTab('needs-action');
        setPageNumber(1);
      } else {
        await updateContactRequest(contactDialog.item.id, details);
        message.success('Đã lưu thông tin liên hệ.');
      }

      setContactDialog(null);
      reload();
    } catch (error) {
      const fallback =
        contactDialog.mode === 'create' ? 'Không thêm được liên hệ.' : 'Không lưu được thông tin liên hệ.';
      message.error(error instanceof ApiError ? error.message : fallback);
    } finally {
      setIsSavingContact(false);
    }
  }

  function confirmDeleteContact(item: ContactRequestItem): void {
    const name = item.fullName || 'không để tên';

    Modal.confirm({
      title: `Xóa liên hệ của ${name}?`,
      content:
        'Liên hệ bị đánh dấu đã xóa ngay trên website và không hiện ở ERP nữa. Dữ liệu không mất hẳn — '
        + 'website vẫn giữ dòng đó — nhưng không khôi phục được từ ERP.',
      okText: 'Xóa liên hệ',
      okButtonProps: { danger: true },
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          await deleteContactRequest(item.id);
          message.success(`Đã xóa liên hệ của ${name}.`);
          reload();
        } catch (error) {
          message.error(error instanceof ApiError ? error.message : 'Không xóa được liên hệ.');
        }
      },
    });
  }

  /** Các thao tác ít dùng, gom vào nút "Thao tác khác" để ô tình trạng giữ được một hàng. */
  function contactMenuItems(item: ContactRequestItem): NonNullable<MenuProps['items']> {
    return [
      {
        key: 'edit',
        icon: <FormOutlined />,
        label: 'Sửa thông tin',
        onClick: () => {
          openEditContact(item);
        },
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Xóa liên hệ',
        danger: true,
        onClick: () => {
          confirmDeleteContact(item);
        },
      },
    ];
  }

  const counts = data?.counts;

  const tabLabel = (label: string, count: number | undefined, highlight = false): ReactNode => (
    <Space size={6}>
      <span>{label}</span>
      {count === undefined ? null : (
        <Tag
          bordered={false}
          color={highlight && count > 0 ? 'gold' : 'default'}
          style={{ marginInlineEnd: 0, borderRadius: 10 }}
        >
          {count.toLocaleString('vi-VN')}
        </Tag>
      )}
    </Space>
  );

  const columns: TableColumnsType<ContactRequestItem> = [
    {
      title: 'Ngày gửi',
      key: 'submittedAt',
      // Chỉ ngày, không giờ phút (rê chuột vẫn thấy đủ). Bớt được 38px so với khi có giờ: 30px cho cột
      // "Khách" để nhãn dịch vụ nằm gọn một hàng, 8px cho cột "Tình trạng"; tổng bảng giữ 1130px.
      width: 112,
      render: (_, item) => (
        <Space direction="vertical" size={0}>
          <span title={formatDateTime(item.submittedAt)}>{formatLocalDate(item.submittedAt)}</span>
          {/* Liên hệ quá hạn thì số ngày chờ đã nằm ở nhãn đỏ cột "Tình trạng", ở đây không nhắc lại. */}
          {overdueDays(item) === null ? ageLabel(item) : null}
        </Space>
      ),
    },
    {
      title: 'Khách',
      key: 'customer',
      width: 220,
      render: (_, item) => {
        const serviceName =
          item.service === null || item.service === '' ? null : (serviceNames[item.service] ?? item.service);
        const serviceStyle = item.service === null ? undefined : serviceTagStyles[item.service];

        return (
          <Space direction="vertical" size={4}>
            <Typography.Text strong>{item.fullName === '' ? '(Không để tên)' : item.fullName}</Typography.Text>
            {serviceName === null && !item.isManual ? null : (
              // Hai nhãn chung một hàng, hàng được xuống dòng: ô chỉ rộng 204px (cột 220 trừ đệm),
              // nhãn dịch vụ dài thì "Nhập tay" sang hàng dưới chứ không làm bảng rộng thêm.
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {serviceName === null ? null : (
                  <Tag
                    bordered={false}
                    color={serviceStyle?.color ?? 'default'}
                    style={{
                      marginInlineEnd: 0,
                      whiteSpace: 'normal',
                      ...(serviceStyle?.strong === true ? { fontWeight: 600 } : {}),
                    }}
                  >
                    {serviceName}
                  </Tag>
                )}
                {item.isManual ? (
                  <Tooltip title="Nhân viên nhập tay trên ERP (khách nhắn Zalo, gọi điện…), không phải khách gửi qua form website. Nguồn ghi ở đầu lời nhắn.">
                    <Tag bordered={false} style={{ marginInlineEnd: 0 }}>
                      Nhập tay
                    </Tag>
                  </Tooltip>
                ) : null}
              </div>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Liên lạc',
      key: 'contact',
      width: 296,
      render: (_, item) => (
        <Space direction="vertical" size={2}>
          {item.phone === null || item.phone === '' ? null : (
            <Typography.Text copyable={{ text: item.phone, tooltips: ['Sao chép số', 'Đã chép'] }}>
              <a href={`tel:${item.phone.replace(/[^\d+]/g, '')}`}>
                <PhoneOutlined /> {item.phone}
              </a>
            </Typography.Text>
          )}
          {item.email === null || item.email === '' ? null : (
            /*
              Cắt chữ bằng CSS ngay trên liên kết, KHÔNG dùng chế độ cắt chữ của Typography: chế độ đó
              cắt theo từng khối con, nên với một liên kết (biểu tượng + địa chỉ) nó bỏ cả liên kết và
              chỉ còn "…". Ở đây email hiện đầy đủ, chỉ email quá dài mới có "…" ở cuối; rê chuột
              vẫn thấy đủ. Bề rộng 256: ô còn 280 (cột 296 trừ đệm 8px hai bên), chừa chỗ cho nút sao
              chép.
            */
            <Typography.Text copyable={{ text: item.email, tooltips: ['Sao chép email', 'Đã chép'] }}>
              <a href={`mailto:${item.email}`} title={item.email} className="erp-truncate" style={{ maxWidth: 256 }}>
                <MailOutlined /> {item.email}
              </a>
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Lời nhắn',
      dataIndex: 'message',
      width: 194,
      render: (value: string | null) =>
        value === null || value === '' ? (
          <Typography.Text type="secondary">—</Typography.Text>
        ) : (
          // Bề rộng cố định để lời nhắn dài tự xuống dòng thay vì kéo giãn cả bảng.
          <Typography.Paragraph
            style={{ marginBottom: 0, whiteSpace: 'pre-line', width: 174 }}
            ellipsis={{ rows: 2, expandable: true, symbol: 'Xem thêm' }}
          >
            {value}
          </Typography.Paragraph>
        ),
    },
    {
      title: 'Tình trạng',
      key: 'status',
      width: 308,
      render: (_, item) => (
        // Khung có bề rộng cố định để dòng người ghi và ghi chú tự xuống dòng thay vì kéo giãn bảng.
        // Nút ghi kết quả và nút "Thao tác khác" nằm ngay trong ô này, cùng hàng với nhãn tình trạng,
        // chứ không thành cột riêng: thêm một cột là bảng tràn khỏi màn hình laptop, phải cuộn
        // ngang mới bấm được. Tổng bảng giữ 1130px.
        // Đo bằng font Inter: nhãn dài nhất "12 ngày chưa liên hệ" 137px + "Ghi kết quả" 114px + nút
        // "Thao tác khác" 24px + khoảng cách 12px = 287px, vừa ô 292px (cột 308 trừ đệm 8px hai bên).
        // Hàng vẫn được phép xuống dòng để phòng font khác rộng hơn (hay chờ từ 100 ngày): khi đó hai
        // nút sang hàng dưới, vẫn nằm bên phải, chứ không tràn ra ngoài ô.
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, width: 292 }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '4px 8px',
            }}
          >
            {statusTag(item)}
            {canManage ? (
              <Space size={4} style={{ marginInlineStart: 'auto' }}>
                <Button
                  type={item.status === ContactFollowUpStatus.New ? 'primary' : 'default'}
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => {
                    openRecord(item);
                  }}
                >
                  Ghi kết quả
                </Button>
                <Dropdown menu={{ items: contactMenuItems(item) }} trigger={['click']} placement="bottomRight">
                  <Button size="small" type="text" icon={<MoreOutlined />} aria-label="Thao tác khác" />
                </Dropdown>
              </Space>
            ) : null}
          </div>
          {item.handledAt === null ? null : (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {item.handledByName ?? 'Không rõ người ghi'} · {formatDateTime(item.handledAt)}
            </Typography.Text>
          )}
          {item.note === null ? null : (
            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: 12, marginBottom: 0, fontStyle: 'italic' }}
              ellipsis={{ rows: 2, tooltip: item.note }}
            >
              {item.note}
            </Typography.Paragraph>
          )}
        </div>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }} align="start" wrap>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Liên hệ khách hàng
          </Typography.Title>
          <Typography.Text type="secondary">
            Khách để lại thông tin ở form liên hệ trên website công ty. Gọi lại rồi ghi kết quả để
            không ai bị bỏ sót.
          </Typography.Text>
        </div>

        <Space wrap>
          <Input.Search
            allowClear
            placeholder="Tìm tên, số điện thoại, email, lời nhắn"
            style={{ width: 300 }}
            onSearch={(value) => {
              setPageNumber(1);
              setSearchTerm(value.trim());
            }}
          />
          <Tooltip title="Tải lại">
            <Button icon={<ReloadOutlined />} onClick={reload} aria-label="Tải lại" />
          </Tooltip>
          {canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateContact}>
              Thêm liên hệ
            </Button>
          ) : null}
        </Space>
      </Space>

      {errorMessage !== null && <Alert type="error" showIcon message={errorMessage} />}

      <Tabs
        activeKey={tab}
        onChange={(key) => {
          setTab(key as TabKey);
          setPageNumber(1);
        }}
        style={{ marginBottom: -16 }}
        items={[
          { key: 'needs-action', label: tabLabel('Cần xử lý', counts?.needsAction, true) },
          { key: 'new', label: tabLabel('Chưa liên hệ', counts?.new, true) },
          { key: 'in-progress', label: tabLabel('Đang liên hệ', counts?.inProgress) },
          { key: 'unreachable', label: tabLabel('Không liên lạc được', counts?.unreachable) },
          { key: 'contacted', label: tabLabel('Đã liên hệ', counts?.contacted) },
          { key: 'irrelevant', label: tabLabel('Không phù hợp', counts?.irrelevant) },
          { key: 'all', label: tabLabel('Tất cả', counts?.total) },
        ]}
      />

      <Table<ContactRequestItem>
        rowKey="id"
        size="small"
        columns={columns}
        dataSource={data === null ? [] : [...data.items]}
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText:
            tab === 'needs-action' && searchTerm === ''
              ? 'Không còn liên hệ nào cần xử lý.'
              : 'Không có liên hệ nào khớp.',
        }}
        pagination={{
          current: data?.pageNumber ?? pageNumber,
          pageSize,
          total: data?.totalCount ?? 0,
          showSizeChanger: false,
          showTotal: (total, range) =>
            `${range[0].toLocaleString('vi-VN')}–${range[1].toLocaleString('vi-VN')}`
            + ` trong ${total.toLocaleString('vi-VN')}`,
          onChange: setPageNumber,
        }}
      />

      <Modal
        title={editing === null ? 'Ghi kết quả' : `Ghi kết quả — ${editing.fullName || 'liên hệ'}`}
        open={editing !== null}
        okText="Lưu"
        cancelText="Hủy"
        confirmLoading={isSaving}
        onOk={() => {
          void saveRecord();
        }}
        onCancel={() => {
          setEditing(null);
        }}
        destroyOnHidden
      >
        {editing === null ? null : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Typography.Text type="secondary">
              {[editing.phone, editing.email].filter((value) => value !== null && value !== '').join(' · ')}
            </Typography.Text>

            <Radio.Group
              value={selectedStatus}
              onChange={(event) => {
                setSelectedStatus(event.target.value as ContactFollowUpStatusValue);
              }}
            >
              <Space direction="vertical">
                {statusOptions.map((status) => (
                  <Radio key={status} value={status}>
                    {statusMeta[status].label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>

            <Input.TextArea
              rows={4}
              maxLength={2000}
              showCount
              // Chừa chỗ cho dòng đếm ký tự, để nó không đè lên hai nút ở chân hộp thoại.
              style={{ marginBottom: 16 }}
              value={note}
              placeholder="Khách cần gì, đã hẹn gì, lần sau gọi lúc nào…"
              onChange={(event) => {
                setNote(event.target.value);
              }}
            />
          </Space>
        )}
      </Modal>

      <Modal
        title={
          contactDialog === null
            ? 'Thông tin liên hệ'
            : contactDialog.mode === 'create'
              ? 'Thêm liên hệ'
              : `Sửa thông tin — ${contactDialog.item.fullName || 'liên hệ'}`
        }
        open={contactDialog !== null}
        okText={isCreatingContact ? 'Thêm' : 'Lưu'}
        cancelText="Hủy"
        confirmLoading={isSavingContact}
        onOk={() => {
          contactForm.submit();
        }}
        onCancel={() => {
          setContactDialog(null);
        }}
        destroyOnHidden
      >
        <Form<ContactFormValues>
          form={contactForm}
          layout="vertical"
          requiredMark={false}
          disabled={isSavingContact}
          onFinish={(values) => {
            void saveContact(values);
          }}
        >
          <Typography.Paragraph type="secondary">
            {isCreatingContact
              ? 'Dành cho khách nhắn Zalo, gọi điện… không qua form website. Liên hệ được thêm thẳng vào '
                + 'dữ liệu liên hệ trên website, nguồn ghi ở đầu lời nhắn — ví dụ “[Zalo] Cần báo giá”.'
              : 'Thay đổi được ghi thẳng vào dữ liệu liên hệ trên website.'}
          </Typography.Paragraph>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 12 }}>
            <Form.Item name="lastName" label="Họ">
              <Input maxLength={maximumFieldLength} />
            </Form.Item>

            <Form.Item
              name="firstName"
              label="Tên"
              dependencies={['lastName']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_rule, value: unknown) =>
                    isBlank(value) && isBlank(getFieldValue('lastName'))
                      ? Promise.reject(new Error('Nhập Họ hoặc Tên.'))
                      : Promise.resolve(),
                }),
              ]}
            >
              <Input maxLength={maximumFieldLength} />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Điện thoại"
              dependencies={['email']}
              rules={[
                ({ getFieldValue }) => ({
                  validator: (_rule, value: unknown) =>
                    isBlank(value) && isBlank(getFieldValue('email'))
                      ? Promise.reject(new Error('Cần ít nhất số điện thoại hoặc email.'))
                      : Promise.resolve(),
                }),
              ]}
            >
              <Input maxLength={maximumFieldLength} />
            </Form.Item>

            <Form.Item
              name="email"
              label="Email"
              rules={[
                {
                  validator: (_rule, value: unknown) =>
                    typeof value === 'string' && value.trim() !== '' && !emailPattern.test(value.trim())
                      ? Promise.reject(new Error('Email không đúng dạng, phải có dạng ten@ten-mien.'))
                      : Promise.resolve(),
                },
              ]}
            >
              <Input maxLength={maximumFieldLength} />
            </Form.Item>
          </div>

          <div
            style={{ display: 'grid', gridTemplateColumns: isCreatingContact ? '1fr 1fr' : '1fr', columnGap: 12 }}
          >
            {isCreatingContact ? (
              <Form.Item name="channel" label="Nguồn">
                <Select<ContactChannelValue> options={[...channelOptions]} />
              </Form.Item>
            ) : null}

            <Form.Item name="service" label="Dịch vụ">
              <Select<string>
                allowClear
                placeholder="Không chọn"
                options={serviceOptionsFor(contactDialog?.mode === 'edit' ? contactDialog.item.service : null)}
              />
            </Form.Item>
          </div>

          <Form.Item name="message" label="Lời nhắn">
            <Input.TextArea
              rows={4}
              maxLength={isCreatingContact ? manualMessageMaximumLength : maximumMessageLength}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
